import { log } from '@/log'
import { PaymentType } from '@/models/app-config/behavior/types'
import { DateWithoutHour } from '@/models/date-time/date-without-hour'
import { getDefaultDeliveryTime } from '@/models/delivery'
import { Either, left, right } from 'fp-ts/lib/Either'
export interface ParsedData {
  // (vuexの)Paymentオブジェクトではない
  paymentParam: PaymentParam,
  cartIds: string[],
  deliveryContactId: string,
  deliveryMethod: string,
  unattendedDelivery: string | null, // 置き配場所コード / e.g. '10'
  deliveryDate: DateWithoutHour | null,
  deliveryTimeSlot: string, // e.g. '0'
  appliedPoint: number,
  appliedCouponId: string | null,
}

export type PaymentParam = CashOnDeliveryPaymentParam
  | CreditCard3DPaymentParam
  | CarrierPaymentParam
  | CvsNumberPaymentParam
  | NpPostPayPaymentParam
  | AtoneParam
  | PayPayParam

interface PaymentParamBase<T extends PaymentType> {
  paymentMethod: T
}
type CashOnDeliveryPaymentParam = PaymentParamBase<'cash-on-delivery'>
type NpPostPayPaymentParam = PaymentParamBase<'np-postpay'>
type AtoneParam = PaymentParamBase<'atone'>
type PayPayParam = PaymentParamBase<'webmoney'>
interface CreditCard3DPaymentParam extends PaymentParamBase<'creditcard3d'> {
  token: string // e.g. 'urn:everforth:commerce:mycolor:card:kuroneko:1'
}
interface CarrierPaymentParam extends PaymentParamBase<'carrier'> {
  carrierProvider: string
}
interface CvsNumberPaymentParam extends PaymentParamBase<'cvs-number'> {
  cvsType: string
}

export const parseRequestData = (data: any): Either<Error, ParsedData> => {
  if (!data) {
    return left(new Error('data must not be empty'))
  }

  const handleError = (key: string) => (error: Error) => {
    log.error({ key, error, value: data[key] })
    throw new Error(`${key}: ${error.message}`)
  }

  try {
    const paymentMethod = Parser.asNotEmptyString(data.paymentMethod).mapLeft(
      handleError('paymentMethod')
    ).value
    const creditCardToken = Parser.asStringNullable(data.creditCardToken).mapLeft(
      handleError('creditCardToken')
    ).value
    const carrierProvider = Parser.asStringNullable(data.carrierProvider).mapLeft(
      handleError('carrierProvider')
    ).value
    const cvsType = Parser.asStringNullable(data.cvsType).mapLeft(
      handleError('carrierProvider')
    ).value
    const paymentParam = createPaymentParam(
      paymentMethod,
      creditCardToken,
      carrierProvider,
      cvsType
    ).mapLeft(
      handleError('paymentParam')
    ).value

    const cartIds = Parser.asCommaSeparatedStrings(data.cartIds).mapLeft(
      handleError('cartIds')
    ).value

    const deliveryMethod = Parser.asNotEmptyString(data.deliveryMethod).mapLeft(
      handleError('deliveryMethod')
    ).value

    const unattendedDelivery = parseUnattendedDelivery(data.unattendedDelivery, deliveryMethod).mapLeft(
      handleError('unattendedDelivery')
    ).value

    const deliveryContactId = Parser.asNotEmptyString(data.deliveryContactId).mapLeft(
      handleError('deliveryContactId')
    ).value

    const deliveryDate = Parser.asDateWithoutHourNullable(data.deliveryDate || null).mapLeft(
      handleError('deliveryDate')
    ).value

    const deliveryTimeSlot = Parser.asStringNullable(data.deliveryTimeSlot).fold(
      handleError('deliveryTimeSlot'),
      nullableString => nullableString || getDefaultDeliveryTime().id // 指定なし
    )

    const appliedPoint = Parser.asNumberNullable(data.appliedPoint || null).fold(
      handleError('appliedPoint'),
      nullableNumber => nullableNumber || 0
    )

    const appliedCouponId = Parser.asStringNullable(data.appliedCouponId || null).mapLeft(
      handleError('appliedCouponId')
    ).value

    return right({
      paymentParam,
      cartIds,
      deliveryMethod,
      unattendedDelivery,
      deliveryContactId,
      deliveryDate,
      deliveryTimeSlot,
      appliedPoint,
      appliedCouponId,
    })
  } catch (error) {
    return left(error as Error)
  }
}

/**
 * リクエストされた値の整合性の検証を行い、PaymentParamとして返却する
 */
const createPaymentParam = (
  paymentMethod: string,
  creditCardToken: string | null,
  carrierProvider: string | null,
  cvsType: string | null
): Either<Error, PaymentParam> => {
  switch (paymentMethod) {
    case 'cash-on-delivery':
    case 'np-postpay':
    case 'atone':
    case 'webmoney':
      return right({ paymentMethod })
    case 'creditcard3d':
      return creditCardToken
        ? right({ paymentMethod, token: creditCardToken })
        : left(new Error(`creditCardToken is not set`))
    case 'carrier':
      return carrierProvider
        ? right({ paymentMethod, carrierProvider })
        : left(new Error(`carrierProvider is not set`))
    case 'cvs-number':
      return cvsType
        ? right({ paymentMethod, cvsType })
        : left(new Error(`cvsType is not set`))
    default:
      return left(new Error(`unsupported payment method (${paymentMethod})`))
  }
}

/**
 * 配送方法が置き配の場合、置き配場所は必須
 */
const parseUnattendedDelivery = (
  value: any,
  deliveryMethod: string
): Either<Error, string | null> => {
  return Parser.asStringNullable(value).chain<string | null>((unattendedDelivery) => {
    if (deliveryMethodIsOkihai(deliveryMethod)) {
      return unattendedDelivery
        ? right<Error, string | null>(unattendedDelivery)
        : left<Error, string | null>(new Error(`unattendedDelivery is required for okihai delivery method (${deliveryMethod})`))
    }

    // 「unattended_deliveryがEngineに送信されるのは置き配のときだけ」という不変条件（Webの注文ページと共通）を
    // アプリ経路でも保つため、置き配以外の配送方法で渡された場合は注文を壊さずに捨てる
    if (unattendedDelivery) {
      log.warn({
        message: 'unattendedDelivery is ignored for non-okihai delivery method',
        deliveryMethod,
        unattendedDelivery,
      })
    }

    return right<Error, string | null>(null)
  })
}

/**
 * NOTE: 配送方法IDの直書きによる判定のため、Engine側に新しい置き配系の配送方法が追加された場合は素通しになる
 * （置き配場所の必須検証が効かなくなるだけで、注文自体の成否はEngine側の検証に従う = fail-open）。
 * 配送方法IDに関心を持つ箇所の一覧は
 * src/Gateway/Model/Delivery/DeliveryMethod/Repository/Engine/Builder/EngineDeliveryMethodBuilder.php
 * のコメントを参照。
 */
const deliveryMethodIsOkihai = (deliveryMethod: string): boolean => {
  return deliveryMethod === 'standard-okihai' || deliveryMethod === 'compact-okihai'
}

class Parser {
  static asNumberNullable(value: any): Either<Error, number | null> {
    if (!value && value !== 0) {
      return right(null)
    }
    const maybeNumber = Number(value)

    return isNaN(maybeNumber)
      ? left(new Error('must be number'))
      : right(maybeNumber)
  }
  static asStringNullable(value: any): Either<Error, string | null> {
    if (!value) {
      return right(null)
    }

    return typeof value !== 'string'
      ? left(new Error('must be type of string'))
      : right(value)
  }
  static asNotEmptyString(value: any): Either<Error, string> {
    return !value || typeof value !== 'string'
      ? left(new Error('must be not empty string'))
      : right(value)
  }
  static asCommaSeparatedStrings(value: any): Either<Error, string[]> {
    return Parser.asNotEmptyString(value).map(str => str.split(','))
  }
  static asDateWithoutHourNullable(value: any): Either<Error, DateWithoutHour | null> {
    if (!value) {
      return right(null)
    }

    try {
      return right(DateWithoutHour.create(value))
    } catch (error) {
      return left(new Error(`must be DateWithoutHour: ${error}`))
    }
  }
}
